/**
 * Configurator block.
 *
 * Reads the block config printed by block-configurator.twig, handles option
 * selection and the running price, keeps the summary in sync, assigns A/B
 * variants, and posts the lead to admin-ajax (action: configurator_submit).
 */

const LOCALES = {no: 'nb-NO', uk: 'en-GB', se: 'sv-SE', dk: 'da-DK', nl: 'nl-NL', be: 'nl-BE', at: 'de-AT'};
const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'msclkid'];

rememberAttribution();
document.querySelectorAll('.configurator').forEach(initConfigurator);

function initConfigurator(section) {
    const configEl = section.querySelector('script.configurator-config');
    const config = configEl ? JSON.parse(configEl.textContent) : {};
    const experiment = config.experiment;

    if (experiment && experiment.id) {
        const assigned = assignVariant(experiment.id);
        if (assigned !== experiment.variant) {
            section.hidden = true;
            return;
        }
        section.hidden = false;
    }

    const state = {};
    const steps = Array.from(section.querySelectorAll('.step-options'));
    const summaries = Array.from(section.querySelectorAll('.selections'));
    const total = section.querySelector('.price-bar .total');
    const form = section.querySelector('form.configurator-form');

    steps.forEach(step => {
        const key = step.dataset.step;
        const single = step.dataset.selection !== 'multiple';
        state[key] = [];

        step.querySelectorAll('.option').forEach(option => {
            if (option.getAttribute('aria-pressed') === 'true') {
                state[key].push(option.dataset.option);
            }

            option.addEventListener('click', () => {
                const value = option.dataset.option;
                const pressed = option.getAttribute('aria-pressed') === 'true';

                if (single) {
                    step.querySelectorAll('.option').forEach(o => o.setAttribute('aria-pressed', 'false'));
                    option.setAttribute('aria-pressed', 'true');
                    state[key] = [value];
                } else {
                    option.setAttribute('aria-pressed', pressed ? 'false' : 'true');
                    state[key] = pressed ? state[key].filter(v => v !== value) : state[key].concat(value);
                }

                render();
                pushEvent('configurator_select', {step: key, option: value, selected: !pressed || single});
            });
        });
    });

    render();
    pushEvent('configurator_view');
    watchPriceBar();

    form?.addEventListener('submit', submit);
    form?.addEventListener('input', hideError);

    // The sticky price bar gets a shadow while it floats over content, i.e.
    // while the sentinel right after it is still below the viewport.
    function watchPriceBar() {
        const bar = section.querySelector('.price-bar');
        const sentinel = section.querySelector('.price-bar-sentinel');
        if (!bar || !sentinel || !('IntersectionObserver' in window)) return;

        new IntersectionObserver(([entry]) => {
            bar.classList.toggle('is-stuck', !entry.isIntersecting && entry.boundingClientRect.top > 0);
        }).observe(sentinel);
    }

    function render() {
        renderSummary();
        renderTotal();
    }

    function renderSummary() {
        if (!summaries.length) return;

        const rows = steps.map(step => {
            const key = step.dataset.step;
            const labels = state[key]
                .map(value => step.querySelector(`.option[data-option="${CSS.escape(value)}"]`)?.dataset.label)
                .filter(Boolean);
            return labels.length ? `<div><dt>${escapeHtml(step.dataset.title)}</dt><dd>${escapeHtml(labels.join(', '))}</dd></div>` : '';
        }).join('');

        summaries.forEach(summary => summary.innerHTML = rows);
    }

    function renderTotal() {
        if (!total || config.priceMode === 'on_request') return;

        let sum = Number(config.fromPrice) || 0;
        steps.forEach(step => {
            state[step.dataset.step].forEach(value => {
                const option = step.querySelector(`.option[data-option="${CSS.escape(value)}"]`);
                sum += Number(option?.dataset.price) || 0;
            });
        });

        const prefix = config.priceMode === 'from' && config.fromLabel ? config.fromLabel + ' ' : '';
        total.textContent = prefix + formatPrice(sum);
    }

    function formatPrice(amount) {
        const locale = LOCALES[config.market] || undefined;
        const number = amount.toLocaleString(locale, {maximumFractionDigits: 0});
        return (config.priceFormat || '{price}').replace('{price}', number);
    }

    function submit(e) {
        e.preventDefault();

        const button = form.querySelector('button[type="submit"]');
        hideError();

        const problem = firstProblem();
        if (problem) {
            showError(problem.code, problem.field);
            return;
        }

        form.querySelector('[name="config"]').value = JSON.stringify(selections());

        const data = new FormData(form);
        Object.entries(attribution()).forEach(([key, value]) => data.append(key, value));
        if (experiment && experiment.id) {
            data.append('experiment_id', experiment.id);
            data.append('experiment_variant', experiment.variant);
        }

        button.disabled = true;

        fetch(easee_scripts.ajax_url, {method: 'POST', body: data, credentials: 'same-origin'})
            .then(async response => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok || json.error) {
                    // The server names the reason and the field, so the
                    // editor's message for that case can be shown.
                    const field = json.field ? form.querySelector(`[name="${CSS.escape(json.field)}"]`) : null;
                    throw Object.assign(new Error(json.error || ''), {code: json.code || 'error', field});
                }
                return json;
            })
            .then(json => {
                pushEvent('installer_quote_submit', {cta_location: 'contact_form', cta_type: 'form_submit'});
                const target = json.redirect || form.querySelector('[name="return_url"]').value;
                if (target) {
                    window.location = target;
                } else {
                    form.replaceWith(Object.assign(document.createElement('p'), {className: 'form-success', textContent: json.message || 'Thank you.'}));
                }
            })
            .catch(error => {
                showError(error.code || 'error', error.field, error.message);
                button.disabled = false;
            });
    }

    /**
     * Mirrors the checks in configurator_submit(): empty fields, email
     * format, phone digit count and the market's postal code pattern.
     */
    function firstProblem() {
        for (const field of form.querySelectorAll('[required]')) {
            if (!field.value.trim()) return {field, code: 'required'};
            if (field.type === 'email' && field.validity.typeMismatch) return {field, code: 'email'};
            if (field.name === 'phone') {
                const digits = field.value.replace(/\D/g, '').length;
                if (digits < Number(field.dataset.minDigits || 7) || digits > Number(field.dataset.maxDigits || 15)) {
                    return {field, code: 'phone'};
                }
            }
            if (field.validity.patternMismatch) return {field, code: field.name};
        }
        return null;
    }

    // Messages come from the block (data-msg-* on the form) so the editor
    // controls the wording; the server's and the browser's texts are fallbacks.
    function showError(code, field, fallback) {
        const errorEl = form.querySelector('.form-error');
        const key = 'msg' + code.replace(/(^|_)([a-z])/g, (match, sep, letter) => letter.toUpperCase());
        errorEl.textContent = form.dataset[key] || fallback || (field && field.validationMessage) || form.dataset.msgError || 'Something went wrong. Please try again.';
        errorEl.classList.add('show');
        if (field) {
            field.classList.add('invalid');
            field.focus();
        }
    }

    function hideError() {
        const errorEl = form.querySelector('.form-error');
        errorEl.textContent = '';
        errorEl.classList.remove('show');
        form.querySelectorAll('.invalid').forEach(el => el.classList.remove('invalid'));
    }

    function selections() {
        const result = {};
        steps.forEach(step => {
            const key = step.dataset.step;
            const single = step.dataset.selection !== 'multiple';
            result[key] = single ? (state[key][0] || null) : state[key];
        });
        return result;
    }

    function pushEvent(event, extra = {}) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({
            event,
            market: config.market,
            experiment_id: experiment?.id || undefined,
            experiment_variant: experiment?.id ? experiment.variant : undefined,
        }, extra));
    }
}

/**
 * Sticky 50/50 assignment per experiment id, kept in a cookie for 90 days so
 * the visitor sees the same variant on every visit.
 */
function assignVariant(id) {
    const name = 'easee_exp_' + id.replace(/[^a-z0-9_-]/gi, '');
    const existing = document.cookie.split('; ').find(c => c.startsWith(name + '='));
    if (existing) {
        return existing.split('=')[1];
    }
    const variant = Math.random() < 0.5 ? 'A' : 'B';
    document.cookie = `${name}=${variant}; max-age=${90 * 24 * 60 * 60}; path=/; SameSite=Lax`;
    return variant;
}

/**
 * Keep campaign parameters from the landing URL for the session, so they
 * reach the lead even when the visitor navigates before submitting.
 */
function rememberAttribution() {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    ATTRIBUTION_KEYS.forEach(key => {
        if (params.get(key)) found[key] = params.get(key);
    });
    if (Object.keys(found).length) {
        try {
            sessionStorage.setItem('easee_attribution', JSON.stringify(found));
        } catch (e) {
            // Storage blocked; the lead simply goes without attribution.
        }
    }
}

function attribution() {
    try {
        return JSON.parse(sessionStorage.getItem('easee_attribution')) || {};
    } catch (e) {
        return {};
    }
}

function escapeHtml(text) {
    return String(text ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
}
